import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, map, Observable, of, tap, catchError, take } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '@htx/environment';
import { HttpUtilsService } from '../http-utils.service';
import { AuthService } from '../auth/auth.service';
import { UserRole } from '../auth/auth.role';
import {
  OrganizationDetails,
  OrganizationDetailsApiDto,
  OrganizationComment,
  OrganizationListItem,
  OrganizationMemberListItem,
  OrganizationMemberDetails,
  OrganizationMemberApiDto,
  OrganizationUpdateDto,
  SpringPageApiDto,
  PageQueryParams,
  PaginatedList,
  PartnerListItem,
  PartnerListItemApiDto,
  PartnerDetail,
  PartnerDetailApiDto,
  AdmissionState
} from './organization.model';

@Injectable({
  providedIn: 'root'
})
export class OrganizationService {
  private httpClient = inject(HttpClient);
  private httpUtils = inject(HttpUtilsService);
  private authService = inject(AuthService);

  private readonly baseUrl = `${environment.backendUrl}/organizations`;
  private readonly membersBaseUrl = `${environment.backendUrl}/members`;

  private readonly admissionState$ = new BehaviorSubject<AdmissionState | null>(null);
  private admissionStateLoaded = false;

  /**
   * Fetch the current user's organization details via GET /organizations/me
   */
  getMyOrganization(): Observable<OrganizationDetails> {
    return this.httpClient.get<OrganizationDetailsApiDto>(`${this.baseUrl}/me`).pipe(
      map((i) => ({
        id: i.organization.id,
        bpnl: i.organization.bpnl,
        did: i.organization.did || '',
        legalName: i.organization.legalName,
        shortName: i.organization.shortName,
        organisationAddress: {
          street: i.organization.street,
          houseNumber: i.organization.houseNumber,
          postalCode: i.organization.postalCode,
          city: i.organization.city,
          country: i.organization.country
        },
        legalForm: i.organization.legalForm,
        description: i.organization.description,
        taxIdNumber: i.organization.taxIdNumber,
        foundingDate: i.organization.foundingDate,
        commercialRegisterNumber: i.organization.commercialRegisterNumber,
        commercialRegisterCourt: i.organization.commercialRegisterCourt,
        website: i.organization.website,
        sector: i.organization.sector,
        admissionState: i.organization.admissionState,
        activityState: i.organization.activityState,
        responsiblePerson: {
          title: i.representative.title,
          firstName: i.representative.firstName,
          lastName: i.representative.lastName,
          email: i.representative.email,
          phoneNumber: i.representative.phoneNumber,
          position: i.representative.position
        }
      }))
    );
  }

  findAllOrganizations(): Observable<OrganizationListItem[]> {
    return this.httpClient
      .get<SpringPageApiDto<OrganizationDetailsApiDto>>(`${this.baseUrl}`)
      .pipe(map((response) => this.mapListItems(response.content)));
  }

  findAllPendingOrganizations(): Observable<OrganizationListItem[]> {
    return this.httpClient
      .get<SpringPageApiDto<OrganizationDetailsApiDto>>(`${this.baseUrl}`, {
        params: { admissionState: 'PENDING' }
      })
      .pipe(map((response) => this.mapListItems(response.content)));
  }

  findAllPartners(): Observable<PartnerListItem[]> {
    return this.httpClient
      .get<SpringPageApiDto<PartnerListItemApiDto>>(`${this.baseUrl}/partners`)
      .pipe(map((response) => response.content));
  }

  /**
   * Fetch paginated organizations with optional filtering and sorting
   * @param queryParams Query parameters including page, size, sort, and filter
   * @returns Observable of paginated list wrapper
   */
  findOrganizationsWithPagination(
    queryParams: PageQueryParams
  ): Observable<PaginatedList<OrganizationListItem>> {
    const httpParams = this.httpUtils.toHttpParams(queryParams);

    return this.httpClient
      .get<SpringPageApiDto<OrganizationDetailsApiDto>>(`${this.baseUrl}`, { params: httpParams })
      .pipe(
        map((response) => ({
          content: this.mapListItems(response.content),
          pagination: {
            pageNumber: response.pageable.pageNumber,
            pageSize: response.pageable.pageSize,
            totalElements: response.totalElements,
            totalPages: response.totalPages,
            last: response.last,
            first: response.first,
            numberOfElements: response.numberOfElements,
            size: response.size,
            number: response.number,
            empty: response.empty
          },
          sort: response.sort
        }))
      );
  }

  /**
   * Fetch paginated partners via the dedicated /organizations/partners endpoint
   * which returns reduced data and is accessible to all authenticated org users.
   * @param queryParams Query parameters including page, size, sort, and optional legalName filter
   * @returns Observable of paginated list wrapper
   */
  findPartnersWithPagination(
    queryParams: PageQueryParams
  ): Observable<PaginatedList<PartnerListItem>> {
    const httpParams = this.httpUtils.toHttpParams(queryParams);

    return this.httpClient
      .get<
        SpringPageApiDto<PartnerListItemApiDto>
      >(`${this.baseUrl}/partners`, { params: httpParams })
      .pipe(
        map((response) => ({
          content: response.content,
          pagination: {
            pageNumber: response.pageable.pageNumber,
            pageSize: response.pageable.pageSize,
            totalElements: response.totalElements,
            totalPages: response.totalPages,
            last: response.last,
            first: response.first,
            numberOfElements: response.numberOfElements,
            size: response.size,
            number: response.number,
            empty: response.empty
          },
          sort: response.sort
        }))
      );
  }

  /**
   * Fetch partner detail via the dedicated /organizations/partners/{id} endpoint.
   * Returns reduced public data, accessible to all authenticated org users.
   */
  getPartnerDetailsById(id: string): Observable<PartnerDetail> {
    return this.httpClient.get<PartnerDetailApiDto>(`${this.baseUrl}/partners/${id}`).pipe(
      map((dto) => ({
        id: dto.id,
        bpnl: dto.bpnl,
        did: dto.did,
        legalName: dto.legalName,
        shortName: dto.shortName,
        sector: dto.sector,
        legalForm: dto.legalForm,
        website: dto.website,
        description: dto.description,
        taxIdNumber: dto.taxIdNumber,
        commercialRegisterNumber: dto.commercialRegisterNumber,
        commercialRegisterCourt: dto.commercialRegisterCourt,
        activityState: dto.activityState,
        address: {
          street: dto.street,
          houseNumber: dto.houseNumber,
          postalCode: dto.postalCode,
          city: dto.city,
          country: dto.country
        },
        representativeName: dto.representativeName,
        representativeEmail: dto.representativeEmail
      }))
    );
  }

  private mapListItems(items: OrganizationDetailsApiDto[]): OrganizationListItem[] {
    return items.map((i: OrganizationDetailsApiDto) => ({
      id: i.organization.id,
      bpnl: i.organization.bpnl,
      legalName: i.organization.legalName,
      shortName: i.organization.shortName,
      admissionState: i.organization.admissionState,
      activityState: i.organization.activityState,
      createdDate: i.organization.createdDate,
      representative: {
        firstName: i.representative.firstName,
        lastName: i.representative.lastName,
        email: i.representative.email,
        phoneNumber: i.representative.phoneNumber
      }
    }));
  }

  getDetailsById(id: string): Observable<OrganizationDetails> {
    return this.httpClient.get<OrganizationDetailsApiDto>(`${this.baseUrl}/${id}`).pipe(
      map((i) => ({
        id: i.organization.id,
        bpnl: i.organization.bpnl,
        did: i.organization.did,
        legalName: i.organization.legalName,
        shortName: i.organization.shortName,
        organisationAddress: {
          street: i.organization.street,
          houseNumber: i.organization.houseNumber,
          postalCode: i.organization.postalCode,
          city: i.organization.city,
          country: i.organization.country
        },
        legalForm: i.organization.legalForm,
        description: i.organization.description,
        taxIdNumber: i.organization.taxIdNumber,
        foundingDate: i.organization.foundingDate,
        commercialRegisterNumber: i.organization.commercialRegisterNumber,
        commercialRegisterCourt: i.organization.commercialRegisterCourt,
        website: i.organization.website,
        sector: i.organization.sector,
        admissionState: i.organization.admissionState,
        activityState: i.organization.activityState,
        responsiblePerson: {
          title: i.representative.title,
          firstName: i.representative.firstName,
          lastName: i.representative.lastName,
          email: i.representative.email,
          phoneNumber: i.representative.phoneNumber,
          position: i.representative.position
        }
      }))
    );
  }

  findMembersOfOrganization(organizationId: string): Observable<OrganizationMemberListItem[]> {
    return this.httpClient
      .get<SpringPageApiDto<OrganizationMemberApiDto>>(`${this.baseUrl}/${organizationId}/members`)
      .pipe(
        map((response) =>
          response.content.map((m) => ({
            id: m.id,
            organizationId: m.organizationId,
            title: m.title,
            firstName: m.firstName,
            lastName: m.lastName,
            position: m.position,
            email: m.email,
            phoneNumber: m.phoneNumber,
            roles: m.roles,
            activityState: m.activityState
          }))
        )
      );
  }

  findMemberDetails(memberId: string): Observable<OrganizationMemberDetails> {
    return this.httpClient.get<OrganizationMemberApiDto>(`${this.membersBaseUrl}/${memberId}`).pipe(
      map((m) => ({
        id: m.id,
        organizationId: m.organizationId,
        iamId: m.iamId,
        title: m.title,
        firstName: m.firstName,
        lastName: m.lastName,
        position: m.position,
        email: m.email,
        phoneNumber: m.phoneNumber,
        roles: m.roles,
        activityState: m.activityState
      }))
    );
  }

  getMyMemberProfile(): Observable<OrganizationMemberDetails> {
    return this.httpClient.get<OrganizationMemberApiDto>(`${this.membersBaseUrl}/me`).pipe(
      map((m) => ({
        id: m.id,
        organizationId: m.organizationId,
        iamId: m.iamId,
        title: m.title,
        firstName: m.firstName,
        lastName: m.lastName,
        position: m.position,
        email: m.email,
        phoneNumber: m.phoneNumber,
        roles: m.roles,
        activityState: m.activityState
      }))
    );
  }

  updateMember(memberId: string, memberData: Partial<OrganizationMemberApiDto>): Observable<void> {
    return this.httpClient.put<void>(`${this.membersBaseUrl}/${memberId}`, memberData);
  }

  createMember(memberData: OrganizationMemberApiDto): Observable<OrganizationMemberApiDto> {
    return this.httpClient.post<OrganizationMemberApiDto>(`${this.membersBaseUrl}`, memberData);
  }

  reject(id: string, reasoning: string): Observable<void> {
    return this.httpClient.put<void>(`${this.baseUrl}/${id}/admission/reject`, { reasoning });
  }

  approve(id: string, reasoning: string): Observable<void> {
    return this.httpClient.put<void>(`${this.baseUrl}/${id}/admission/approve`, { reasoning });
  }

  getComments(id: string): Observable<OrganizationComment[]> {
    return this.httpClient.get<OrganizationComment[]>(`${this.baseUrl}/${id}/admission/comments`);
  }

  addComment(id: string, comment: { text: string }): Observable<OrganizationComment> {
    return this.httpClient.post<OrganizationComment>(
      `${this.baseUrl}/${id}/admission/comments`,
      comment
    );
  }

  getRegistrationDocument(organizationId: string): Observable<Blob> {
    if (environment.useMocks) {
      return new Observable<Blob>((subscriber) => {
        import('../../mocks/data/organizations/mocked-organizations').then((mod) => {
          const blob = mod.getOrganizationDocument(organizationId);
          if (blob) {
            subscriber.next(blob);
            subscriber.complete();
          } else {
            subscriber.error(new Error('Document not found'));
          }
        });
      });
    }
    return this.httpClient.get(`${this.baseUrl}/${organizationId}/document`, {
      responseType: 'blob'
    });
  }

  update(organizationId: string, dto: OrganizationUpdateDto): Observable<string> {
    return this.httpClient
      .put(`${this.baseUrl}/${organizationId}`, dto)
      .pipe(map(() => organizationId));
  }

  /**
   * Returns an Observable<boolean> that emits true if the current user's
   * organization is NOT approved (PENDING or REJECTED).
   * DataspaceAuthority users always get false.
   * The result is cached after the first call.
   */
  isOrganizationPending$(): Observable<boolean> {
    const roles = this.authService.userRoles$.value;
    if (roles.includes(UserRole.DataspaceAuthority)) {
      return of(false);
    }

    if (this.admissionStateLoaded) {
      return this.admissionState$.pipe(
        take(1),
        map(state => state !== null && state !== AdmissionState.APPROVED)
      );
    }

    return this.getMyOrganization().pipe(
      map(org => org.admissionState),
      tap(state => {
        this.admissionState$.next(state);
        this.admissionStateLoaded = true;
      }),
      map(state => state !== AdmissionState.APPROVED),
      catchError(() => {
        this.admissionState$.next(null);
        this.admissionStateLoaded = true;
        return of(false);
      })
    );
  }
}
